import { useEffect, useMemo, useRef } from "react"
import { Document, Page, View, StyleSheet, pdf } from "@react-pdf/renderer"
import BoxLabelWrappedText from "./BoxLabelWrappedText.jsx"
import BoxLabelBarcode from "./BoxLabelBarcode.jsx"
import BoxLabelQrCode from "./BoxLabelQrCode.jsx"
import { HELVETICA_BOLD_CHAR_WIDTH_RATIO, HELVETICA_UPPERCASE_CHAR_WIDTH_RATIO } from "./BoxLabelWrappedText.jsx"
import { buildProcessingReceiptFromOrder } from "../fish-room/buildProcessingReceiptFromOrder.js"
import { mergeLabelPdfWithReceiptsInterleaved } from "../fish-room/buildProcessingReceiptPdf.js"
import { buildLabelPages, normalizeNumCopies } from "./boxLabelData.js"
import { shouldUseBoxLabelsV2 } from "./boxLabelsConfig.js"
import { printBoxLabelsV2 } from "./printBoxLabelsV2.js"
/** 4" × 6" label page format [101.6, 152.4] mm */
const LABEL_WIDTH_MM = 101.6
const LABEL_HEIGHT_MM = 152.4
const MM_TO_PT = 72 / 25.4
const LABEL_SIZE_PT = [LABEL_WIDTH_MM * MM_TO_PT, LABEL_HEIGHT_MM * MM_TO_PT]

/** Original HTML label design width in px; scaled to PDF points. */
const DESIGN_WIDTH_PX = 480
const PX = LABEL_SIZE_PT[0] / DESIGN_WIDTH_PX
const px = (value) => value * PX

/** Label content width (480px design − 10px padding each side). */
const LABEL_CONTENT_WIDTH = px(460)

/** Footer text area beside the QR placeholder (460 − 140 design px). */
const FOOTER_TEXT_MAX_WIDTH = px(320)

/** Barcode display width (460px content − 5px margin each side). */
const BARCODE_DESIGN_WIDTH = 450
const BARCODE_DESIGN_HEIGHT = 120
const QR_DESIGN_SIZE = 140

const styles = StyleSheet.create({
    page: {
        paddingTop: px(10),
        paddingBottom: px(10),
        paddingLeft: px(10),
        paddingRight: px(10),
    },
    column: {
        flexDirection: "column",
        height: "100%",
    },
    centerRow: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
    },
    masterRow: {
        width: "100%",
        flexShrink: 0,
    },
    master: {
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
        width: "100%",
    },
    barcodeRow: {
        marginTop: px(18),
        alignItems: "center",
        flexShrink: 0,
    },
    barcode: {
        width: px(BARCODE_DESIGN_WIDTH),
        height: px(BARCODE_DESIGN_HEIGHT),
    },
    nameSection: {
        flex: 1,
        flexShrink: 1,
        justifyContent: "center",
    },
    customerName: {
        fontSize: px(60),
        lineHeight: px(55) / px(60),
    },
    statusRow: {
        alignItems: "center",
        flexShrink: 0,
    },
    statusBox: {
        marginTop: px(10),
        width: "100%",
        height: px(100),
        borderWidth: 2,
        borderColor: "#000000",
        justifyContent: "center",
        alignItems: "center",
    },
    statusText: {
        fontFamily: "Helvetica-Bold",
        fontSize: px(60),
        lineHeight: px(60) / px(60),
        textAlign: "center",
        width: "100%",
    },
    footerRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-end",
        marginTop: px(10),
        flexShrink: 0,
    },
    bottomSection: {
        flexShrink: 0,
    },
    qr: {
        width: px(QR_DESIGN_SIZE),
        height: px(QR_DESIGN_SIZE),
        flexShrink: 0,
    },
    footerRight: {
        width: FOOTER_TEXT_MAX_WIDTH,
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        flexShrink: 0,
    },
    fishTag: {
        fontSize: px(25),
        lineHeight: px(25) / px(25),
        marginBottom: px(12),
        textAlign: "right",
    },
    locationCode: {
        fontSize: px(45),
        lineHeight: px(45) / px(45),
        textAlign: "right",
    },
    boxCount: {
        fontSize: px(45),
        lineHeight: px(45) / px(45),
        marginTop: px(8),
        textAlign: "right",
    },
})

function masterFontSize(master) {
    return String(master ?? "").length <= 7 ? px(100) : px(70)
}

function masterStyle(master) {
    const fontSize = masterFontSize(master)
    return {
        fontSize,
        lineHeight: 0.8,
    }
}

function LabelPage({ label }) {
    return (
        <Page size={LABEL_SIZE_PT} style={styles.page}>
            <View style={styles.column}>
                <View style={styles.masterRow}>
                    <BoxLabelWrappedText
                        style={[styles.master, masterStyle(label.master)]}
                        maxWidth={LABEL_CONTENT_WIDTH}
                        maxLines={2}
                        charWidthRatio={HELVETICA_BOLD_CHAR_WIDTH_RATIO}
                    >
                        {label.master}
                    </BoxLabelWrappedText>
                </View>

                <View style={[styles.centerRow, styles.barcodeRow]}>
                    <BoxLabelBarcode
                        value={label.master}
                        width={styles.barcode.width}
                        height={styles.barcode.height}
                    />
                </View>

                <View style={styles.nameSection}>
                    <BoxLabelWrappedText
                        style={styles.customerName}
                        maxWidth={LABEL_CONTENT_WIDTH}
                        maxLines={4}
                        charWidthRatio={HELVETICA_UPPERCASE_CHAR_WIDTH_RATIO}
                    >
                        {label.customerName}
                    </BoxLabelWrappedText>
                </View>

                <View style={styles.bottomSection}>
                    {label.status ? (
                        <View style={styles.statusRow}>
                            <View style={styles.statusBox}>
                                <BoxLabelWrappedText
                                    style={styles.statusText}
                                    maxWidth={LABEL_CONTENT_WIDTH}
                                    maxLines={1}
                                    charWidthRatio={HELVETICA_BOLD_CHAR_WIDTH_RATIO}
                                >
                                    {label.status}
                                </BoxLabelWrappedText>
                            </View>
                        </View>
                    ) : null}

                    <View style={styles.footerRow}>
                        <BoxLabelQrCode
                            value={label.qrValue}
                            size={styles.qr.width}
                        />
                        <View style={styles.footerRight}>
                            {label.fishTag ? (
                                <BoxLabelWrappedText
                                    style={styles.fishTag}
                                    maxWidth={FOOTER_TEXT_MAX_WIDTH}
                                    maxLines={1}
                                >
                                    {label.fishTag}
                                </BoxLabelWrappedText>
                            ) : null}
                            <BoxLabelWrappedText
                                style={styles.locationCode}
                                maxWidth={FOOTER_TEXT_MAX_WIDTH}
                                maxLines={1}
                            >
                                {label.locationCode}
                            </BoxLabelWrappedText>
                            <BoxLabelWrappedText
                                style={styles.boxCount}
                                maxWidth={FOOTER_TEXT_MAX_WIDTH}
                                maxLines={1}
                            >
                                {`Box ${label.boxNum} of ${label.totalBoxes}`}
                            </BoxLabelWrappedText>
                        </View>
                    </View>
                </View>
            </View>
        </Page>
    )
}

function BoxLabelsDocument({ labels }) {
    return (
        <Document>
            {labels.map((label, index) => (
                <LabelPage key={index} label={label} />
            ))}
        </Document>
    )
}

/**
 * Box labels — PDF (@react-pdf/renderer) by default, or HTML print dialog when
 * VITE_USE_BOX_LABELS_V2=true and the active location's use_print_dialog is enabled.
 *
 * @param {{
 *   orders: any[],
 *   locations?: any[],
 *   helpers?: any,
 *   openPdf: boolean,
 *   setOpenPdf: (open: boolean) => void,
 *   progress?: number | null,
 *   setProgress?: ((value: number) => void) | null,
 *   cancelRef?: { current: boolean } | null,
 *   numCopies?: number,
 *   printProcessingReceipt?: boolean,
 *   app?: any,
 * }} props
 */
export default function BoxLabels({
    orders,
    locations = [],
    helpers = {},
    openPdf,
    setOpenPdf,
    progress = null,
    setProgress = null,
    cancelRef = null,
    numCopies = 1,
    printProcessingReceipt = false,
    app = null,
}) {
    const helpersRef = useRef(helpers)
    helpersRef.current = helpers

    const ordersRef = useRef(orders)
    ordersRef.current = orders

    const locationsRef = useRef(locations)
    locationsRef.current = locations

    const appRef = useRef(app)
    appRef.current = app

    const printProcessingReceiptRef = useRef(printProcessingReceipt)
    printProcessingReceiptRef.current = printProcessingReceipt

    const setProgressRef = useRef(setProgress)
    setProgressRef.current = typeof setProgress === "function" ? setProgress : () => {}

    const setOpenPdfRef = useRef(setOpenPdf)
    setOpenPdfRef.current = setOpenPdf

    const copies = normalizeNumCopies(numCopies)
    const useV2 = shouldUseBoxLabelsV2({
        locations,
        locationId: app?.locationId ?? null,
    })

    const ordersKey = useMemo(() => {
        if (!orders?.length) {
            return ""
        }
        return orders
            .map((order) => `${order?.id ?? ""}:${order?.total_boxes ?? 0}`)
            .join("|")
    }, [orders])

    useEffect(() => {
        if (!openPdf || !ordersKey) {
            return
        }

        let cancelled = false
        const isCancelled = () => Boolean(cancelRef?.current) || cancelled

        if (cancelRef) {
            cancelRef.current = false
        }

        const updateProgress = (value) => {
            setProgressRef.current(value)
        }

        updateProgress(0)

        if (useV2) {
            printBoxLabelsV2({
                orders: ordersRef.current,
                locations: locationsRef.current,
                helpers: helpersRef.current,
                numCopies: copies,
                printProcessingReceipt: printProcessingReceiptRef.current,
                app: appRef.current,
                cancelRef,
                onProgress: updateProgress,
            })
                .catch((error) => {
                    console.error("BoxLabels v2 print failed:", error)
                })
                .finally(() => {
                    if (!isCancelled()) {
                        setOpenPdfRef.current(false)
                    }
                })

            return () => {
                cancelled = true
            }
        }

        const pages = buildLabelPages(ordersRef.current, locationsRef.current, helpersRef.current)
        const labelList = copies <= 1
            ? pages
            : pages.flatMap((page) => Array.from({ length: copies }, () => page))

        if (labelList.length === 0) {
            console.warn("BoxLabels: no labels to print — check total_boxes on selected orders.")
            updateProgress(100)
            setOpenPdfRef.current(false)
            return
        }

        pdf(<BoxLabelsDocument labels={labelList} />)
            .toBlob()
            .then(async (blob) => {
                if (isCancelled()) {
                    return
                }

                let outputBlob = blob
                if (printProcessingReceiptRef.current && ordersRef.current?.length) {
                    const segments = ordersRef.current.map((order) => {
                        const totalBoxes = Math.max(0, Math.floor(Number(order?.total_boxes) || 0))
                        return {
                            pageCount: totalBoxes * copies,
                            receipt: buildProcessingReceiptFromOrder(
                                order,
                                order?.total_boxes,
                                appRef.current,
                                helpersRef.current
                            ),
                        }
                    })
                    const labelBytes = new Uint8Array(await blob.arrayBuffer())
                    const mergedBytes = await mergeLabelPdfWithReceiptsInterleaved(
                        labelBytes,
                        segments
                    )
                    outputBlob = new Blob([mergedBytes], { type: "application/pdf" })
                }

                if (isCancelled()) {
                    return
                }
                updateProgress(100)
                const fileURL = URL.createObjectURL(outputBlob)
                window.open(fileURL)
            })
            .catch((error) => {
                console.error("BoxLabels PDF generation failed:", error)
            })
            .finally(() => {
                if (!isCancelled()) {
                    setOpenPdfRef.current(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [openPdf, ordersKey, copies, cancelRef, printProcessingReceipt, useV2, locations, app?.locationId])

    return null
}
